import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { CalendarDays, Plane, ArrowRight } from "lucide-react";

function useCountdown(target) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

const Unit = ({ n, label }) => (
  <div className="flex flex-col items-center px-3 py-2 rounded-md bg-white/8 border border-white/12 min-w-[56px]">
    <div className="font-serif text-2xl lg:text-3xl text-accent tabular-nums leading-none">
      {String(n).padStart(2, "0")}
    </div>
    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{label}</div>
  </div>
);

const CountdownCard = ({ pkg, index }) => {
  const { lang } = useLang();
  const { days, hours, minutes, seconds, expired } = useCountdown(pkg.departure_date);
  const name = lang === "id" ? pkg.name_id : pkg.name_en;
  const price = lang === "id" ? pkg.price_label_id : pkg.price_label_en;
  const dateStr = new Date(pkg.departure_date).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-[#0f0f0f] to-[#1c1a17] hover:from-[#161515] hover:to-[#221f1a] transition-all duration-300 anim-fade-up"
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`countdown-card-${pkg.slug}`}
    >
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: `url(${pkg.image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="relative p-7 lg:p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full ${pkg.category === "haji" ? "bg-accent/20 text-accent" : "bg-white/10 text-white/80"}`}>
            {pkg.category === "haji" ? "Haji" : "Umrah"}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-white/50">{pkg.duration}</span>
        </div>

        <h3 className="font-serif text-xl lg:text-2xl text-white leading-tight mb-2">{name}</h3>

        <div className="flex items-center gap-2 text-white/70 text-sm mt-3 mb-6">
          <CalendarDays className="h-4 w-4 text-accent shrink-0" />
          <span>{dateStr}</span>
        </div>

        {expired ? (
          <div className="text-sm text-white/60 py-4">Keberangkatan telah lewat.</div>
        ) : (
          <div className="flex gap-2 mb-6" data-testid={`countdown-timer-${pkg.slug}`}>
            <Unit n={days} label={lang === "id" ? "Hari" : "Days"} />
            <Unit n={hours} label={lang === "id" ? "Jam" : "Hrs"} />
            <Unit n={minutes} label={lang === "id" ? "Menit" : "Min"} />
            <Unit n={seconds} label={lang === "id" ? "Detik" : "Sec"} />
          </div>
        )}

        <div className="flex items-center justify-between pt-5 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">
              {lang === "id" ? "Mulai" : "From"}
            </div>
            <div className="font-serif text-lg text-accent mt-0.5">{price}</div>
          </div>
          <Link
            to={`/packages/${pkg.slug}`}
            className="inline-flex items-center gap-1.5 text-white text-sm hover:text-accent transition-colors"
            data-testid={`countdown-cta-${pkg.slug}`}
          >
            {lang === "id" ? "Detail" : "Details"} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function CountdownSection() {
  const { lang } = useLang();
  const [pkgs, setPkgs] = React.useState([]);

  React.useEffect(() => {
    api.get("/packages")
      .then((r) => {
        const now = Date.now();
        const upcoming = r.data
          .filter((p) => {
            if (!p.departure_date) return false;
            if (new Date(p.departure_date).getTime() <= now) return false;
            // Skip packages yang kuotanya sudah penuh — auto-shift ke paket berikutnya
            if (p.full_booked === true) return false;
            if (typeof p.seats_remaining === "number" && p.seats_remaining <= 0) return false;
            return true;
          })
          .sort((a, b) => new Date(a.departure_date) - new Date(b.departure_date))
          .slice(0, 3);
        setPkgs(upcoming);
      })
      .catch(() => {});
  }, []);

  if (pkgs.length === 0) return null;

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden bg-[#0a0a0a]" data-testid="countdown-section">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/70 to-transparent" />
      <div className="container-x relative">
        <header className="max-w-2xl mb-14">
          <div className="flex items-center gap-4 mb-4">
            <span className="h-px w-10 bg-accent" />
            <p className="overline !text-accent">
              {lang === "id" ? "Jadwal Keberangkatan" : "Departure Schedule"}
            </p>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-white tracking-tight text-balance">
            {lang === "id"
              ? "Segera Berangkat ke Tanah Suci"
              : "Departing Soon to the Holy Land"}
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed">
            <Plane className="inline h-4 w-4 mr-1 text-accent" />
            {lang === "id"
              ? "Tiga jadwal keberangkatan terdekat. Segera daftar sebelum kuota penuh."
              : "The three nearest departures. Book now before seats run out."}
          </p>
        </header>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pkgs.map((p, i) => (
            <CountdownCard key={p.id} pkg={p} index={i} />
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
    </section>
  );
}
